/**
 * InstrumentBezel — the MERIDIAN application shell (one continuous machined object).
 *
 * This is NOT "a sidebar plus a header". It is a single piece of Liquid Glass bent
 * into an L: a fixed top spine + a collapsible left spine, joined by one sacred,
 * fixed corner. The glass is authored as ONE clipped surface (`.ib-glass`) so there
 * is one material, one blur, one rim highlight, one shadow, and one specular that
 * wraps the bend — no seam where the arms meet. Navigation is engraved into the
 * left spine; the global command center is engraved into the top spine; the logo
 * lives permanently in the corner and never moves or collapses.
 *
 * Mounted ONCE, globally, in AppShell — never re-rendered per route — so every page
 * is genuinely mounted into the same instrument. The map is the environment behind
 * the glass; it does not tint it.
 *
 * DOM is an implementation detail: the one-glass illusion is the contract, achieved
 * with a single clip-pathed surface + children that carry no glass of their own.
 */

import { useEffect, useRef, useState } from 'react';
import { useUserSession } from '../../providers/UserSessionProvider';
import { useAppStore } from '../../store/appStore';
import { useMapPreferencesStore } from '../../store/mapPreferencesStore';
import type { MapProjection } from '../../store/mapPreferencesStore';
import { useLiquidGlass } from '../../hooks/useLiquidGlass';
import { tzLabel, ALL_TIMEZONES } from '../../utils/timezone';
import { CollapsiblePanel } from './CollapsiblePanel';
import { NotificationCenter } from '../shared/NotificationCenter';
import {
  MeridianMark,
  Search,
  Chat,
  ChevronLeft,
  ChevronDown,
  Logout,
  ThemeLight,
  ThemeDark,
  ThemeSystem,
  MapFlat,
  Globe,
} from '../icons';
import './InstrumentBezel.css';

// ── Theme control (compact; engraved into the top spine) ──
type ThemePref = 'light' | 'dark' | 'system';
const THEME_KEY = 'stationExplorer_theme';
function applyTheme(pref: ThemePref) {
  const root = document.documentElement;
  if (pref === 'system') {
    root.removeAttribute('data-theme');
    root.style.colorScheme = '';
  } else {
    root.setAttribute('data-theme', pref);
    root.style.colorScheme = pref;
  }
}

// Precompute timezone options once — label shows "City/Region (UTC+N)" so users
// know the offset without opening the selector. Computed at module load time so
// it never re-runs on re-render. ~600 entries.
const TZ_OPTIONS: { value: string; label: string; offset: string }[] = ALL_TIMEZONES.map(tz => ({
  value: tz,
  label: tz.replace(/_/g, ' '),
  offset: tzLabel(tz),
}));

/** Open the global command palette (Cmd/Ctrl+K). The palette also listens for this
 *  custom event so the visible search field is a real, discoverable trigger. */
function openCommandPalette() {
  window.dispatchEvent(new CustomEvent('meridian:open-command-palette'));
}

/** Open NORA, the Station Operator chat panel (also Cmd/Ctrl+J). ChatPanel listens for
 *  this event so the bezel button is a discoverable trigger for the same panel. */
function openChat() {
  window.dispatchEvent(new CustomEvent('meridian:open-chat'));
}

export const InstrumentBezel = () => {
  const { userEmail, logout } = useUserSession();
  const { isPanelCollapsed, togglePanel } = useAppStore();
  const [theme, setTheme] = useState<ThemePref>(
    () => (typeof localStorage !== 'undefined' && (localStorage.getItem(THEME_KEY) as ThemePref)) || 'system'
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement | null>(null);
  const mapProjection = useMapPreferencesStore((s) => s.projection);
  const setMapProjection = useMapPreferencesStore((s) => s.setProjection);
  const timezone = useMapPreferencesStore((s) => s.timezone);
  const setTimezone = useMapPreferencesStore((s) => s.setTimezone);
  const use24HourTime = useMapPreferencesStore((s) => s.use24HourTime);
  const setUse24HourTime = useMapPreferencesStore((s) => s.setUse24HourTime);
  const [tzOpen, setTzOpen] = useState(false);
  const [tzSearch, setTzSearch] = useState('');
  const tzRef = useRef<HTMLDivElement | null>(null);

  // Close tz picker on outside click / Escape
  useEffect(() => {
    if (!tzOpen) return;
    const onDown = (e: MouseEvent) => {
      if (tzRef.current && !tzRef.current.contains(e.target as Node)) setTzOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setTzOpen(false); setTzSearch(''); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [tzOpen]);

  // Liquid Glass refraction on the profile dropdown. It hangs below the top spine
  // over real page content, so its edges have a backdrop to lens. `enabled` tracks
  // `profileOpen` so the hook's effect fires when the menu mounts and tears down on
  // close. Non-Chromium / reduced-transparency / high-contrast / Calm all fall back
  // to the CSS glass on `.ib-profile__menu` (solid under the a11y floor — see .css).
  // NOTE: the search pill (.ib-search) is deliberately NOT wired — it sits directly
  // on the bezel's own 24px-blurred .ib-glass, so there is no crisp backdrop behind
  // its edges to refract; the hook's own scope note excludes the shell for this reason.
  const { ref: profileGlassRef } = useLiquidGlass<HTMLDivElement>({ enabled: profileOpen, radius: 16 });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Close the profile menu on outside click / Escape.
  useEffect(() => {
    if (!profileOpen) return;
    const onDown = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setProfileOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [profileOpen]);

  const themeOptions: { id: ThemePref; label: string; Glyph: React.FC }[] = [
    { id: 'light', label: 'Light', Glyph: ThemeLight },
    { id: 'dark', label: 'Dark', Glyph: ThemeDark },
    { id: 'system', label: 'System', Glyph: ThemeSystem },
  ];

  const projectionOptions: { id: MapProjection; label: string; Glyph: React.FC }[] = [
    { id: 'mercator', label: 'Flat', Glyph: MapFlat },
    { id: 'globe', label: 'Globe', Glyph: Globe },
  ];

  return (
    <div className={`instrument-bezel${isPanelCollapsed ? ' is-collapsed' : ''}`}>
      {/* THE ONE GLASS — a single clip-pathed L surface. One material/blur/rim/
          shadow/specular. Everything else is mounted ON it and carries no glass. */}
      <div className="ib-glass" aria-hidden="true" />
      {/* The continuous specular that wraps the outer bend (top-left). */}
      <div className="ib-specular" aria-hidden="true" />

      {/* SACRED CORNER — logo anchor. Fixed size, never moves/collapses. */}
      <div className="ib-corner">
        <span className="ib-logo" aria-label="Station Explorer">
          <MeridianMark size="lg" />
        </span>
      </div>

      {/* TOP SPINE — the global command center, engraved into the top arm. */}
      <header className="ib-top" role="banner">
        <button
          type="button"
          className="ib-search"
          onClick={openCommandPalette}
          aria-keyshortcuts="Meta+K Control+K"
          aria-label="Search or go to"
        >
          <Search size="sm" />
          <span className="ib-search__label">Search or go to…</span>
        </button>

        <div className="ib-top__trailing">
          <span className="ib-status" role="status" aria-label="Live">
            <span className="ib-status__dot" />
            <span className="ib-status__text">Live</span>
          </span>

          {/* NORA, the Station Operator chat — a discoverable trigger for the panel (Cmd/Ctrl+J). */}
          <button
            type="button"
            className="ib-chat"
            onClick={openChat}
            aria-keyshortcuts="Meta+J Control+J"
            aria-label="Ask NORA, the Station Operator"
            title="Ask NORA, the Station Operator"
          >
            <Chat size="sm" />
          </button>

          {/* Notification Center — the durable log of app-wide issues (bell + popover). */}
          <NotificationCenter />
          {/* Profile menu — the home for identity + preferences: explicit theme
              switcher (light / dark / system) and sign-out. */}
          <div className="ib-profile" ref={profileRef}>
            <button
              type="button"
              className="ib-user"
              onClick={() => setProfileOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              aria-label={`Account: ${userEmail || 'not signed in'}`}
              title={userEmail || 'Not signed in'}
            >
              <span className="ib-user__avatar">{userEmail ? userEmail.charAt(0).toUpperCase() : '?'}</span>
            </button>

            {profileOpen && (
              <div className="ib-profile__menu" role="menu" ref={profileGlassRef}>
                <div className="ib-profile__email">{userEmail || 'Not signed in'}</div>

                <div className="ib-profile__section">
                  <span className="ib-profile__label">Appearance</span>
                  <div className="ib-theme-switch" role="radiogroup" aria-label="Theme preference">
                    {themeOptions.map(({ id, label, Glyph }) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={theme === id}
                        className={`ib-theme-switch__btn${theme === id ? ' is-active' : ''}`}
                        onClick={() => setTheme(id)}
                        title={label}
                      >
                        <Glyph />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ib-profile__section">
                  <span className="ib-profile__label">Map projection</span>
                  <div className="ib-theme-switch" role="radiogroup" aria-label="Map projection">
                    {projectionOptions.map(({ id, label, Glyph }) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={mapProjection === id}
                        className={`ib-theme-switch__btn${mapProjection === id ? ' is-active' : ''}`}
                        onClick={() => setMapProjection(id)}
                        title={label}
                      >
                        <Glyph />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ib-profile__section" ref={tzRef}>
                  <span className="ib-profile__label">
                    Timezone
                  </span>
                  {/* Trigger button — shows current selection + offset, opens the picker */}
                  <button
                    type="button"
                    className="ib-tz-trigger"
                    onClick={() => { setTzOpen(v => !v); setTzSearch(''); }}
                    aria-haspopup="listbox"
                    aria-expanded={tzOpen}
                  >
                    <span className="ib-tz-trigger__name">
                      {TZ_OPTIONS.find(o => o.value === timezone)?.label ?? timezone}
                    </span>
                    <span className="ib-tz-trigger__offset">{tzLabel(timezone)}</span>
                    <ChevronDown size={14} />
                  </button>
                  {/* Dropdown panel */}
                  {tzOpen && (
                    <div className="ib-tz-dropdown" role="listbox" aria-label="Timezone">
                      <input
                        type="search"
                        className="ib-tz-search"
                        placeholder="Search…"
                        value={tzSearch}
                        onChange={e => setTzSearch(e.target.value)}
                        aria-label="Search timezones"
                        autoFocus
                      />
                      <div className="ib-tz-list">
                        {(() => {
                          const q = tzSearch.toLowerCase();
                          const filtered = q ? TZ_OPTIONS.filter(o => o.label.toLowerCase().includes(q)) : TZ_OPTIONS;
                          if (filtered.length === 0) return <span className="ib-tz-empty">No results</span>;
                          return filtered.map(o => (
                            <button
                              key={o.value}
                              type="button"
                              role="option"
                              aria-selected={timezone === o.value}
                              className={`ib-tz-option${timezone === o.value ? ' is-active' : ''}`}
                              onClick={() => { setTimezone(o.value); setTzOpen(false); setTzSearch(''); }}
                            >
                              <span className="ib-tz-option__name">{o.label}</span>
                              <span className="ib-tz-option__offset">{o.offset}</span>
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Time format toggle */}
                <div className="ib-profile__section">
                  <span className="ib-profile__label">Time format</span>
                  <div className="ib-theme-switch" role="radiogroup" aria-label="Time format">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={use24HourTime}
                      className={`ib-theme-switch__btn${use24HourTime ? ' is-active' : ''}`}
                      onClick={() => setUse24HourTime(true)}
                      title="24-hour"
                    >
                      <span>24h</span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!use24HourTime}
                      className={`ib-theme-switch__btn${!use24HourTime ? ' is-active' : ''}`}
                      onClick={() => setUse24HourTime(false)}
                      title="12-hour AM/PM"
                    >
                      <span>12h</span>
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  role="menuitem"
                  className="ib-profile__signout"
                  onClick={() => { setProfileOpen(false); logout(); }}
                >
                  <Logout size="sm" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* LEFT SPINE — navigation engraved into the left arm. Reuses the tested
          CollapsiblePanel nav (keyboard model, disclosure groups, portaled flyouts);
          its own toggle + footer are suppressed via CSS since the bezel owns those. */}
      <nav className="ib-left" aria-label="Primary">
        <CollapsiblePanel />
        <button
          type="button"
          className="ib-collapse"
          onClick={togglePanel}
          aria-label={isPanelCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!isPanelCollapsed}
        >
          <span className="ib-collapse__glyph"><ChevronLeft size={18} /></span>
          {/* Always rendered so it FADES on collapse (like nav labels) rather than
              unmounting instantly — CSS handles the opacity + the rail clips it. */}
          <span className="ib-collapse__label">Collapse</span>
        </button>
      </nav>
    </div>
  );
};
